<script setup>
import { ref, computed, onMounted } from 'vue';
import controleAcessoAPI from 'dashboard/api/controleAcesso';
import { useAlert } from 'dashboard/composables';
import { ACCESS_CONTROL_CATALOG } from 'dashboard/constants/accessControl.js';

const ROLE_LABELS = {
  administrator: 'Administradores',
  supervisor: 'Supervisores',
  operacional: 'Operacional',
  agent: 'Agentes',
  // v101: 3 perfis novos (veem todas as conversas das caixas onde sao membros)
  qualidade: 'Qualidade',
  ouvidoria: 'Ouvidoria',
  juridico: 'Jurídico',
};
const BASE_ROLES = [
  'administrator',
  'supervisor',
  'operacional',
  'agent',
  'qualidade',
  'ouvidoria',
  'juridico',
];

const catalog = ACCESS_CONTROL_CATALOG;

const loading = ref(false);
const saving = ref(false);
const usuarios = ref([]);
const customRoles = ref([]);
const selectedProfile = ref('administrator'); // role string ou `custom:<id>`
const selectedUserId = ref(null);
const blockedSet = ref(new Set());

const profiles = computed(() => {
  const list = BASE_ROLES.map(role => ({ key: role, label: ROLE_LABELS[role] }));
  customRoles.value.forEach(cr =>
    list.push({ key: `custom:${cr.id}`, label: cr.name })
  );
  return list;
});

const usersOfProfile = computed(() =>
  usuarios.value.filter(user => {
    if (selectedProfile.value.startsWith('custom:')) {
      const id = Number(selectedProfile.value.split(':')[1]);
      return user.custom_role_id === id;
    }
    // papel nativo: ignora quem tem custom role
    return user.role === selectedProfile.value && !user.custom_role_id;
  })
);

const ALL_SENTINEL = '__ALL__';
const isAllSelected = computed(() => selectedUserId.value === ALL_SENTINEL);
const currentProfileLabel = computed(
  () => (profiles.value.find(p => p.key === selectedProfile.value) || {}).label || ''
);

const selectedUser = computed(
  () => usuarios.value.find(user => user.id === selectedUserId.value) || null
);

const isAllowed = key => !blockedSet.value.has(key);

const allKeysOf = node => {
  const keys = [node.key];
  (node.children || []).forEach(child => keys.push(...allKeysOf(child)));
  return keys;
};

// Marcar/desmarcar um no. Ao desmarcar, bloqueia o no e TODOS os filhos (cascata)
// para o guard de rota tambem bloquear as sub-rotas. Ao marcar, libera tudo.
const toggleKey = node => {
  const keys = allKeysOf(node);
  const next = new Set(blockedSet.value);
  if (isAllowed(node.key)) {
    keys.forEach(key => next.add(key));
  } else {
    keys.forEach(key => next.delete(key));
  }
  blockedSet.value = next;
};

const setSection = (section, allow) => {
  const keys = allKeysOf(section);
  const next = new Set(blockedSet.value);
  keys.forEach(key => (allow ? next.delete(key) : next.add(key)));
  blockedSet.value = next;
};

const selectUser = user => {
  selectedUserId.value = user.id;
  blockedSet.value = new Set(user.blocked || []);
};

// Card "TODOS": configura em lote para todos os usuarios do perfil. Comeca com
// tudo liberado; ao salvar, sobrescreve a config de todos do perfil (menos id 1).
// PADRONIZAR (v85): padroes por perfil vindos do backend; checkbox do card
// TODOS. Marcado ao aplicar -> vira padrao do perfil (usuario NOVO daquele
// perfil ja nasce com esses bloqueios). Desmarcado ao aplicar -> remove.
const padroes = ref({});
const padronizar = ref(false);

// FUNCOES RESTRITAS (v87): WhatsApp ao vivo, menu Meta e Horario de Uso —
// bloqueadas por padrao; id 1 marca AQUI quem pode usar cada uma.
const FUNCOES_RESTRITAS = [
  { key: 'live_whatsapp', label: 'WhatsApp ao vivo (Campanhas)' },
  { key: 'meta_menu', label: 'Menu Meta (Template / Disparo / Histórico)' },
  { key: 'horario_uso', label: 'Horário de Uso' },
];
const restritaSel = ref({ live_whatsapp: {}, meta_menu: {}, horario_uso: {} });
const salvandoRestrita = ref('');

const carregarRestritas = mapa => {
  FUNCOES_RESTRITAS.forEach(f => {
    const marcados = {};
    (mapa?.[f.key] || []).forEach(id => {
      marcados[id] = true;
    });
    restritaSel.value[f.key] = marcados;
  });
};

const salvarRestrita = async funcao => {
  salvandoRestrita.value = funcao;
  try {
    const ids = Object.keys(restritaSel.value[funcao])
      .filter(id => restritaSel.value[funcao][id])
      .map(Number);
    await controleAcessoAPI.salvarFuncaoRestrita(funcao, ids);
    useAlert('Função restrita salva — vale no próximo acesso de cada usuário.');
  } catch (error) {
    useAlert('Erro ao salvar função restrita.');
  } finally {
    salvandoRestrita.value = '';
  }
};

const selectAll = () => {
  selectedUserId.value = ALL_SENTINEL;
  blockedSet.value = new Set();
  padronizar.value = !!padroes.value[selectedProfile.value];
};

const load = async () => {
  loading.value = true;
  try {
    const { data } = await controleAcessoAPI.fetch();
    usuarios.value = data.usuarios || [];
    customRoles.value = data.custom_roles || [];
    padroes.value = data.padroes || {};
    carregarRestritas(data.funcoes_restritas);
  } catch (error) {
    useAlert('Erro ao carregar usuários.');
  } finally {
    loading.value = false;
  }
};

const save = async () => {
  if (!isAllSelected.value && !selectedUser.value) return;
  const blocklist = Array.from(blockedSet.value);

  // Lote (card TODOS): aplica a mesma config a todos do perfil, sobrescrevendo
  // ajustes individuais. Confirma antes (acao destrutiva).
  if (isAllSelected.value) {
    const ids = usersOfProfile.value.map(user => user.id);
    if (!ids.length) {
      useAlert('Nenhum usuário neste perfil.');
      return;
    }
    // eslint-disable-next-line no-alert
    const ok = window.confirm(
      `Aplicar esta configuração a TODOS os ${ids.length} usuário(s) de "${currentProfileLabel.value}"? Isso substitui ajustes individuais. (O super admin id 1 não é afetado.)`
    );
    if (!ok) return;
    saving.value = true;
    try {
      const { data } = await controleAcessoAPI.save({
        user_ids: ids,
        blocklist,
        padronizar: padronizar.value,
        profile_key: selectedProfile.value,
      });
      usuarios.value.forEach(user => {
        if (ids.includes(user.id)) user.blocked = blocklist;
      });
      padroes.value = data.padroes || padroes.value;
      useAlert(
        padronizar.value
          ? 'Aplicado a todos e PADRONIZADO: usuário novo deste perfil já nasce com esses bloqueios.'
          : 'Aplicado a todos do perfil. Vale no próximo acesso de cada um.'
      );
    } catch (error) {
      useAlert('Erro ao salvar.');
    } finally {
      saving.value = false;
    }
    return;
  }

  // Individual.
  saving.value = true;
  try {
    await controleAcessoAPI.save({ user_id: selectedUser.value.id, blocklist });
    const stored = usuarios.value.find(user => user.id === selectedUser.value.id);
    if (stored) stored.blocked = blocklist;
    useAlert('Permissões salvas. Valem no próximo acesso do usuário.');
  } catch (error) {
    useAlert('Erro ao salvar.');
  } finally {
    saving.value = false;
  }
};

onMounted(load);
</script>

<template>
  <div class="flex flex-col gap-4 p-6 h-full w-full overflow-auto bg-n-background text-n-slate-12">
    <header class="flex flex-col gap-1">
      <h1 class="text-xl font-semibold">Controle de Acesso</h1>
      <p class="text-sm text-n-slate-11 max-w-3xl">
        Defina o que cada usuário enxerga no sistema. Verde = tem acesso; ao
        desmarcar, a opção some da tela dele. Vale no próximo acesso do usuário.
        O super admin (id 1) não aparece e nunca é afetado.
      </p>
    </header>

    <div v-if="loading" class="text-n-slate-11">Carregando…</div>

    <template v-else>
      <!-- Seletor de perfil -->
      <div class="flex flex-wrap gap-2">
        <button
          v-for="profile in profiles"
          :key="profile.key"
          class="px-3 py-1 text-sm rounded-lg border border-n-weak"
          :class="
            selectedProfile === profile.key
              ? 'bg-n-brand text-white border-n-brand'
              : 'bg-n-solid-2 text-n-slate-12 hover:bg-n-alpha-1'
          "
          @click="
            selectedProfile = profile.key;
            selectedUserId = null;
          "
        >
          {{ profile.label }}
        </button>
      </div>

      <div class="flex flex-1 gap-6 min-h-0">
        <!-- Lista de usuarios do perfil -->
        <div class="flex-shrink-0 p-2 w-64 overflow-auto rounded-lg border border-n-weak">
          <!-- Card TODOS: aplica em lote a todos do perfil -->
          <button
            class="px-2 py-2 mb-2 w-full text-left rounded-lg border border-dashed border-n-weak"
            :class="
              isAllSelected
                ? 'bg-n-brand/10 outline outline-1 outline-n-brand'
                : 'hover:bg-n-alpha-1'
            "
            @click="selectAll"
          >
            <div class="flex gap-2 items-center font-semibold">
              <span class="i-lucide-users size-4" />
              <span>TODOS deste perfil</span>
            </div>
            <div class="text-xs text-n-slate-11">
              Aplica a {{ usersOfProfile.length }} usuário(s) de uma vez
            </div>
          </button>
          <p
            v-if="!usersOfProfile.length"
            class="p-2 text-sm text-n-slate-11"
          >
            Nenhum usuário neste perfil.
          </p>
          <button
            v-for="user in usersOfProfile"
            :key="user.id"
            class="px-2 py-2 mb-1 w-full text-left rounded-lg"
            :class="
              selectedUserId === user.id
                ? 'bg-n-brand/10 outline outline-1 outline-n-brand'
                : 'hover:bg-n-alpha-1'
            "
            @click="selectUser(user)"
          >
            <div class="font-medium truncate">{{ user.name }}</div>
            <div class="text-xs truncate text-n-slate-11">{{ user.email }}</div>
          </button>
        </div>

        <!-- Arvore de funcoes -->
        <div class="flex-1 p-3 overflow-auto rounded-lg border border-n-weak">
          <div v-if="!selectedUser && !isAllSelected" class="p-2 text-n-slate-11">
            Selecione um usuário (ou "TODOS deste perfil") para configurar o acesso.
          </div>
          <div v-else class="flex flex-col gap-4">
            <div
              v-if="isAllSelected"
              class="p-3 text-sm rounded-lg border border-yellow-200 bg-yellow-50 text-yellow-800"
            >
              Configurando <strong>TODOS</strong> os {{ usersOfProfile.length }}
              usuário(s) de "{{ currentProfileLabel }}". Ao salvar, esta configuração
              <strong>substitui</strong> os ajustes individuais (o super admin id 1
              não é afetado).
            </div>
            <!-- PADRONIZAR (v85): usuario novo do perfil nasce com esses bloqueios -->
            <label
              v-if="isAllSelected"
              class="flex gap-2 items-start p-3 text-sm rounded-lg border border-n-weak cursor-pointer"
            >
              <input v-model="padronizar" type="checkbox" class="mt-0.5" />
              <span>
                <strong>DESEJA PADRONIZAR ESSAS ALTERAÇÕES?</strong><br />
                <span class="text-n-slate-11">
                  Marcado: todo usuário <strong>novo</strong> criado no perfil
                  "{{ currentProfileLabel }}" já nasce com estes bloqueios
                  aplicados automaticamente. Desmarcado: remove o padrão deste
                  perfil (novos usuários nascem sem bloqueio).
                </span>
              </span>
            </label>
            <div
              v-for="section in catalog"
              :key="section.key"
              class="p-3 rounded-lg border border-n-weak"
            >
              <div class="flex justify-between items-center mb-2">
                <label class="flex gap-2 items-center font-semibold cursor-pointer">
                  <input
                    type="checkbox"
                    :checked="isAllowed(section.key)"
                    @change="toggleKey(section)"
                  />
                  <span>{{ section.label }}</span>
                </label>
                <div v-if="section.children" class="flex gap-3 text-xs">
                  <button class="underline text-n-slate-11" @click="setSection(section, true)">
                    Marcar todos
                  </button>
                  <button class="underline text-n-slate-11" @click="setSection(section, false)">
                    Desmarcar todos
                  </button>
                </div>
              </div>
              <div
                v-if="section.children"
                class="grid grid-cols-1 gap-2 pl-6 sm:grid-cols-2"
              >
                <label
                  v-for="child in section.children"
                  :key="child.key"
                  class="flex gap-2 items-center cursor-pointer"
                >
                  <input
                    type="checkbox"
                    :checked="isAllowed(child.key)"
                    @change="toggleKey(child)"
                  />
                  <span
                    class="inline-block rounded-sm size-3"
                    :class="isAllowed(child.key) ? 'bg-green-500' : 'bg-n-slate-5'"
                  />
                  <span>{{ child.label }}</span>
                </label>
              </div>
            </div>

            <div class="sticky bottom-0 py-2 bg-n-background">
              <button
                class="px-4 py-2 text-white rounded-lg bg-n-brand disabled:opacity-50"
                :disabled="saving"
                @click="save"
              >
                {{ saving ? 'Salvando…' : 'Salvar' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- ============ FUNCOES RESTRITAS (v87) ============ -->
    <div class="mt-8 p-4 rounded-lg border border-n-weak">
      <h2 class="text-lg font-semibold text-n-slate-12 mb-1">
        Funções restritas — liberar por usuário
      </h2>
      <p class="text-sm text-n-slate-11 mb-4">
        Estas funções ficam <strong>bloqueadas para todo mundo</strong> (menos
        você). Marque quem pode usar cada uma e salve. Vale no próximo acesso.
      </p>
      <div class="grid gap-4 lg:grid-cols-3">
        <div
          v-for="funcao in FUNCOES_RESTRITAS"
          :key="funcao.key"
          class="p-3 rounded-lg border border-n-weak flex flex-col gap-2"
        >
          <p class="font-medium text-n-slate-12">{{ funcao.label }}</p>
          <div class="max-h-64 overflow-y-auto flex flex-col gap-1">
            <label
              v-for="user in usuarios"
              :key="`${funcao.key}-${user.id}`"
              class="flex items-center gap-2 text-sm text-n-slate-12 cursor-pointer"
            >
              <input
                v-model="restritaSel[funcao.key][user.id]"
                type="checkbox"
              />
              <span class="truncate">{{ user.name }}</span>
              <span class="text-xs text-n-slate-10 truncate">{{ user.email }}</span>
            </label>
          </div>
          <button
            class="self-end px-4 py-1.5 text-white rounded-lg bg-n-brand disabled:opacity-50 text-sm"
            :disabled="salvandoRestrita === funcao.key"
            @click="salvarRestrita(funcao.key)"
          >
            {{ salvandoRestrita === funcao.key ? 'Salvando…' : 'Salvar' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
